import { type ReactNode } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

export type JsmPlaygroundSidePanelProps = {
	isOpen: boolean;
	children?: ReactNode;
	testId?: string;
};

/**
 * The collapsible column a product screen opens beside its table — a request's details, a survey
 * response. Opening it pushes the table narrower rather than overlaying it.
 *
 * Always mounted so its width can animate, and taken out of the tab order and the a11y tree while
 * collapsed. The panel is laid out at its full width from the start and revealed by the track, so
 * its contents never reflow mid-animation.
 *
 * Place it in a flex row beside the table. It stretches to the row, whose height the table sets:
 * the panel itself contributes no height, so a long panel scrolls inside rather than growing past
 * the table's bottom edge.
 */
export const JsmPlaygroundSidePanel = ({
	isOpen,
	children,
	testId,
}: JsmPlaygroundSidePanelProps) => (
	<div
		css={[trackStyles, isOpen && trackOpenStyles]}
		data-testid={testId}
		aria-hidden={!isOpen}
		// @ts-expect-error -- inert is valid HTML but is missing from @compiled/react types
		inert={isOpen ? undefined : ''}
	>
		<div css={panelSlotStyles}>{children}</div>
	</div>
);

const PANEL_WIDTH = '348px';
const PANEL_GUTTER = token('space.300');

const trackStyles = css({
	position: 'relative',
	flexShrink: 0,
	alignSelf: 'stretch',
	width: 0,
	opacity: 0,
	overflow: 'hidden',
	transition: 'width 260ms cubic-bezier(0.2, 0, 0, 1), opacity 180ms ease-out',
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
});

const trackOpenStyles = css({
	width: `calc(${PANEL_WIDTH} + ${PANEL_GUTTER})`,
	opacity: 1,
});

const panelSlotStyles = css({
	display: 'flex',
	position: 'absolute',
	insetBlock: 0,
	insetInlineEnd: 0,
	width: PANEL_WIDTH,
});
